import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Loading from "components/Loading";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import DataLoadErrorAlert from "components/common/DataLoadErrorAlert";
import { useAuth } from "hooks/useAuth";
import { useFdeRole } from "hooks/useFdeRole";
import {
  ANNOTATION_STATUS_LABELS,
  listReturnedCandidatesForTcis,
} from "services/fde/annotationService";
import { FDE_ROLE_LABELS, listTciRegistry } from "services/fde/fdeRegistryService";
import { incompleteModule4TciIds } from "services/fde/roleOnboardingService";
import { HELP_VIDEO_WORKFLOWS } from "services/helpVideoService";
import { useRegisterHelpWorkflow } from "hooks/useRegisterHelpWorkflow";
import { db } from "services/firebase";
import {
  formatSessionProductLabel,
  formatSessionTargetLabel,
} from "pages/dashboard/sessions/sessionDisplayLabels";

const SessionListPage = () => {
  useRegisterHelpWorkflow(HELP_VIDEO_WORKFLOWS.FDEC_SESSION_REPLAY);
  const { organizationId, userData } = useAuth();
  const { isFdeC, fdeCTciIds, loading: fdeLoading } = useFdeRole();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [tcis, setTcis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionsLoadError, setSessionsLoadError] = useState(null);
  const [tcisLoadError, setTcisLoadError] = useState(null);
  const [candidatesLoadError, setCandidatesLoadError] = useState(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [tciFilter, setTciFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [returnedCandidates, setReturnedCandidates] = useState([]);
  const [dismissModule4Nudge, setDismissModule4Nudge] = useState(false);

  const incompleteModule4 = useMemo(
    () => (isFdeC ? incompleteModule4TciIds(userData, fdeCTciIds) : []),
    [isFdeC, userData, fdeCTciIds],
  );

  const isTeacherOrAdmin = ["admin", "teacher"].includes(
    userData?.activeOrgRole?.role || userData?.role,
  );
  const canAccess = isTeacherOrAdmin || isFdeC;

  const institutionId =
    userData?.activeInstitutionId ||
    userData?.institutionId ||
    organizationId ||
    null;

  useEffect(() => {
    setTcisLoadError(null);
    listTciRegistry({ institutionId })
      .then((rows) => {
        setTcis(rows);
        setTcisLoadError(null);
      })
      .catch((err) => {
        console.warn("[SessionListPage] TCI registry failed", err);
        setTcis([]);
        setTcisLoadError(
          err?.message || "Unable to load TCI registry. Filters may be incomplete.",
        );
      });
  }, [institutionId, reloadNonce]);

  useEffect(() => {
    if (!institutionId || !isFdeC || !fdeCTciIds.length) {
      setReturnedCandidates([]);
      setCandidatesLoadError(null);
      return undefined;
    }
    let cancelled = false;
    setCandidatesLoadError(null);
    listReturnedCandidatesForTcis({ institutionId, tciIds: fdeCTciIds })
      .then((rows) => {
        if (!cancelled) {
          setReturnedCandidates(rows);
          setCandidatesLoadError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setReturnedCandidates([]);
          setCandidatesLoadError(
            err?.message ||
              "Unable to load returned candidates. An empty banner does not mean none exist.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [institutionId, isFdeC, fdeCTciIds, reloadNonce]);

  useEffect(() => {
    if (!institutionId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setSessionsLoadError(null);
    // Query by institutionId so list rules can prove access via callerInstitutionId().
    db.collection("conversationSessions")
      .where("institutionId", "==", institutionId)
      .limit(100)
      .get()
      .then((snap) => {
        setSessions(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        setSessionsLoadError(null);
      })
      .catch((err) => {
        console.warn("[SessionListPage] session query failed", err);
        setSessions([]);
        setSessionsLoadError(
          err?.message ||
            "Unable to load sessions. An empty list here is a load failure, not “no sessions.”",
        );
      })
      .finally(() => setLoading(false));
  }, [institutionId, reloadNonce]);

  const tciNameById = useMemo(() => {
    const map = new Map();
    tcis.forEach((tci) => map.set(tci.id, tci.name || tci.id));
    return map;
  }, [tcis]);

  const visibleTciOptions = useMemo(() => {
    if (isFdeC && fdeCTciIds.length) {
      return tcis.filter((tci) => fdeCTciIds.includes(tci.id));
    }
    return tcis;
  }, [tcis, fdeCTciIds, isFdeC]);

  const filteredSessions = useMemo(() => {
    let rows = sessions;
    if (isFdeC && fdeCTciIds.length) {
      rows = rows.filter((session) => session.tciId && fdeCTciIds.includes(session.tciId));
    }
    if (tciFilter) {
      rows = rows.filter((session) => session.tciId === tciFilter);
    }
    if (statusFilter) {
      rows = rows.filter(
        (session) => (session.annotationStatus || "pending") === statusFilter,
      );
    }
    return rows;
  }, [sessions, isFdeC, fdeCTciIds, tciFilter, statusFilter]);

  if (fdeLoading || loading) return <Loading page />;

  if (!canAccess) {
    return (
      <Typography color="text.secondary">
        {FDE_ROLE_LABELS["fde-c"]}, teacher, or admin access is required to review
        sessions.
      </Typography>
    );
  }

  return (
    <Box>
      <DashboardPageHeader
        title="Conversation sessions"
        subtitle={
          isFdeC
            ? "Review AI interactions for your assigned TCIs."
            : "Review AI-conducted conversations within your organization."
        }
      />

      {returnedCandidates.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Returned candidates need your follow-up
          </Typography>
          <Stack spacing={1}>
            {returnedCandidates.map((row) => (
              <Box
                key={row.id}
                sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}
              >
                <Typography variant="body2">
                  {row.promptId} — {row.returnQuestions || row.reviewNotes || "Manager questions pending"}
                </Typography>
                {row.sourceSessionId && (
                  <Button
                    size="small"
                    onClick={() => navigate(`/dashboard/sessions/${row.sourceSessionId}`)}
                  >
                    Open session
                  </Button>
                )}
              </Box>
            ))}
          </Stack>
        </Alert>
      )}

      {!dismissModule4Nudge && incompleteModule4.length > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          onClose={() => setDismissModule4Nudge(true)}
        >
          <Typography variant="subtitle2" gutterBottom>
            Module 4 soft readiness
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            You can still annotate. When you have a moment, complete Module 4 for:{" "}
            {incompleteModule4.join(", ")}.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {incompleteModule4.map((tciId) => (
              <Button
                key={tciId}
                size="small"
                onClick={() => navigate(`/dashboard/training-decks/${tciId}`)}
              >
                Open Module 4 — {tciId}
              </Button>
            ))}
          </Stack>
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="tci-filter-label">TCI</InputLabel>
          <Select
            labelId="tci-filter-label"
            label="TCI"
            value={tciFilter}
            onChange={(event) => setTciFilter(event.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {visibleTciOptions.map((tci) => (
              <MenuItem key={tci.id} value={tci.id}>
                {tci.name || tci.id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="status-filter-label">Annotation status</InputLabel>
          <Select
            labelId="status-filter-label"
            label="Annotation status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {Object.entries(ANNOTATION_STATUS_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {sessionsLoadError ? (
        <DataLoadErrorAlert
          message={sessionsLoadError}
          onRetry={() => setReloadNonce((n) => n + 1)}
        />
      ) : null}
      {tcisLoadError ? (
        <DataLoadErrorAlert
          message={tcisLoadError}
          onRetry={() => setReloadNonce((n) => n + 1)}
        />
      ) : null}
      {candidatesLoadError ? (
        <DataLoadErrorAlert
          message={candidatesLoadError}
          onRetry={() => setReloadNonce((n) => n + 1)}
        />
      ) : null}

      <Paper data-testid="fdec-session-list">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>TCI</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Target</TableCell>
              <TableCell>Annotation</TableCell>
              <TableCell>Turns</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!sessionsLoadError && filteredSessions.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    No sessions match the current filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {sessionsLoadError && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="error">
                    Session list unavailable — see error above.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {filteredSessions.map((session) => (
              <TableRow
                key={session.id}
                hover
                data-testid={`fdec-session-row-${session.id}`}
                sx={{ cursor: "pointer" }}
                onClick={() => navigate(`/dashboard/sessions/${session.id}`)}
              >
                <TableCell>
                  {session.tciId ? tciNameById.get(session.tciId) || session.tciId : "—"}
                </TableCell>
                <TableCell>{formatSessionProductLabel(session.productId)}</TableCell>
                <TableCell>
                  {formatSessionTargetLabel(session.conversationTarget)}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={
                      ANNOTATION_STATUS_LABELS[session.annotationStatus || "pending"] ||
                      session.annotationStatus ||
                      "Pending"
                    }
                  />
                </TableCell>
                <TableCell>{session.turns?.length ?? 0}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default SessionListPage;
